/**
 * Un día de la agenda es un sobre + huecos (breaks). Quien edita piensa en
 * **tramos**: mañana y tarde. Aquí se traduce de uno al otro.
 */
import { aHhmm, aMinutos } from '../routes/calendario-geometria'

export type Tramo = { start: string; end: string }
export type Franja = { start: string; end: string; breaks: Tramo[] } | null
export type Plan = Record<
    'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
    Franja
>

const PASO = 15
const MINIMO = 30

export function pegarMinuto(m: number): number {
    const t = Math.max(0, Math.min(24 * 60 - 1, Math.round(m / PASO) * PASO))
    return t
}

export function tramosDeFranja(f: Franja): Tramo[] {
    if (!f) return []
    const a = aMinutos(f.start)
    const b = aMinutos(f.end)
    if (a === null || b === null || b <= a) return []
    const cortes = [...(f.breaks ?? [])]
        .map((br) => {
            const x = aMinutos(br.start)
            const y = aMinutos(br.end)
            if (x === null || y === null || y <= x) return null
            return { x: Math.max(a, x), y: Math.min(b, y) }
        })
        .filter((x): x is { x: number; y: number } => x !== null && x.y > x.x)
        .sort((p, q) => p.x - q.x)

    const out: Tramo[] = []
    let cursor = a
    for (const c of cortes) {
        if (c.x > cursor) out.push({ start: aHhmm(cursor), end: aHhmm(c.x) })
        cursor = Math.max(cursor, c.y)
    }
    if (b > cursor) out.push({ start: aHhmm(cursor), end: aHhmm(b) })
    return out
}

export function franjaDeTramos(tramos: readonly Tramo[]): Franja {
    const limpios = [...tramos]
        .map((t) => {
            const a = aMinutos(t.start)
            const b = aMinutos(t.end)
            if (a === null || b === null || b <= a) return null
            return { a, b }
        })
        .filter((x): x is { a: number; b: number } => x !== null)
        .sort((p, q) => p.a - q.a)
    if (limpios.length === 0) return null

    const fundidos: { a: number; b: number }[] = []
    for (const t of limpios) {
        const last = fundidos[fundidos.length - 1]
        if (last && t.a <= last.b) last.b = Math.max(last.b, t.b)
        else fundidos.push({ ...t })
    }

    const start = fundidos[0]!.a
    const end = fundidos[fundidos.length - 1]!.b
    const breaks: Tramo[] = []
    for (let i = 0; i < fundidos.length - 1; i++) {
        const huecoA = fundidos[i]!.b
        const huecoB = fundidos[i + 1]!.a
        if (huecoB > huecoA) breaks.push({ start: aHhmm(huecoA), end: aHhmm(huecoB) })
    }
    return { start: aHhmm(start), end: aHhmm(end), breaks }
}

/** 9–18 de un solo tramo → mañana y tarde, el partido de toda la vida. */
export function partirTramos(tramos: readonly Tramo[]): Tramo[] | null {
    if (tramos.length !== 1) return null
    const a = aMinutos(tramos[0]!.start)
    const b = aMinutos(tramos[0]!.end)
    if (a === null || b === null) return null
    if (b - a >= 8 * 60) {
        return [
            { start: aHhmm(a), end: '14:00' },
            { start: '17:00', end: b <= 18 * 60 ? '20:00' : aHhmm(b) },
        ]
    }
    if (b - a < 3 * 60) return null
    const hueco = 60
    const cada = Math.floor((b - a - hueco) / 2)
    if (cada < MINIMO) return null
    return [
        { start: aHhmm(a), end: aHhmm(a + cada) },
        { start: aHhmm(a + cada + hueco), end: aHhmm(b) },
    ]
}

export function ajustarExtremo(
    tramos: readonly Tramo[],
    indice: number,
    cual: 'start' | 'end',
    minutos: number,
): Tramo[] {
    const t = tramos.map((x) => ({ ...x }))
    const cur = t[indice]
    if (!cur) return t
    const a = aMinutos(cur.start)
    const b = aMinutos(cur.end)
    if (a === null || b === null) return t
    let v = pegarMinuto(minutos)
    if (cual === 'start') {
        v = Math.max(0, Math.min(v, b - MINIMO))
        cur.start = aHhmm(v)
    } else {
        v = Math.min(24 * 60 - PASO, Math.max(v, a + MINIMO))
        cur.end = aHhmm(v)
    }
    return t
}

export function quitarTramo(tramos: readonly Tramo[], indice: number): Tramo[] {
    return tramos.filter((_, i) => i !== indice)
}

/** Un segundo tramo detrás del último, con una hora de hueco. */
export function añadirTramo(tramos: readonly Tramo[]): Tramo[] {
    if (tramos.length === 0) return [{ start: '09:00', end: '14:00' }]
    if (tramos.length >= 2) return tramos.map((t) => ({ ...t }))
    const b = aMinutos(tramos[tramos.length - 1]!.end)
    if (b === null) return tramos.map((t) => ({ ...t }))
    const start = Math.min(b + 60, 20 * 60)
    const end = Math.min(start + 3 * 60, 22 * 60)
    if (end - start < MINIMO) return tramos.map((t) => ({ ...t }))
    return [...tramos, { start: aHhmm(start), end: aHhmm(end) }]
}

export function textoTramos(tramos: readonly Tramo[]): string {
    if (tramos.length === 0) return 'cerrado'
    return tramos.map((t) => `${t.start}–${t.end}`).join(' / ')
}

const LABORABLES: (keyof Plan)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
const FINDE: (keyof Plan)[] = ['saturday', 'sunday']

export function grupoDelDia(dia: keyof Plan): readonly (keyof Plan)[] {
    return LABORABLES.includes(dia) ? LABORABLES : FINDE
}

export function hayRestoAbierto(plan: Plan, origen: keyof Plan): boolean {
    return grupoDelDia(origen).some((d) => d !== origen && tramosDeFranja(plan[d]).length > 0)
}

/** Copia el día al resto abierto del grupo. No abre un cerrado. */
export function copiarDiaAlResto(plan: Plan, origen: keyof Plan): Plan {
    const patron = plan[origen]
    const next: Plan = { ...plan }
    for (const d of grupoDelDia(origen)) {
        if (d === origen) continue
        if (tramosDeFranja(plan[d]).length === 0) continue
        next[d] =
            patron === null
                ? null
                : { start: patron.start, end: patron.end, breaks: patron.breaks.map((b) => ({ ...b })) }
    }
    return next
}
